// The Saju sample book as a Studio output (see output-panel.mjs). The published pages are
// the thumbnails export-studio-images.py writes; previews, pages large enough to read and
// apply come from the local preview server only (apps/course-ui/scripts/saju_book_workshop.py).
import { SAJU_BOOK_FRAME, SAJU_CHARTS, SAJU_DOC_FIELDS, SAJU_PRODUCTS, SAJU_PRODUCT_COPY, isSajuDocField, sajuDocText, setSajuDocText } from './templates.mjs';
import { runOutputJob } from './output-jobs.mjs';
import { documentDesignEdits } from './document-design-edits.mjs';

const WORKSHOP = '/__saju/book';

// A book is its template's pages and the parts the builder adds around them, told apart by
// the layouts that make each (build-product-samples.py; SKILL "Book length": the core, then
// 대운 10, 세운, 월운). The book's own 차례 (book-contents) names the same parts.
const CHAPTER_OF_LAYOUT = Object.freeze({
  'book-boundary': 'front', 'book-contents': 'front',
  'native-personality-strengths-cautions': 'native', 'personality-inner-tension': 'native',
  'native-wealth-four-traits': 'native', 'wealth-value-boundary': 'native',
  'native-daeun-open-timeline': 'daeun', 'daeun-read': 'daeun', 'daeun-advice': 'daeun',
  'seun-map': 'seun', 'seun-phase': 'seun', 'seun-year': 'seun', 'century-decade': 'seun',
  'wolun-table': 'wolun', 'wolun-quarter': 'wolun',
  'demo-form': 'demo',
  worksheet: 'appendix', formula: 'appendix', uncalculated: 'appendix', colophon: 'appendix',
});
// name: what the book calls it; note: what it is made of.
const CHAPTERS = Object.freeze({
  front: { name: '책 머리', note: '계산한 것과 하지 않은 것, 이 책의 차례 · 모든 책이 같은 틀' },
  native: { name: '상품 전용 쪽', note: '이 상품에만 있는 인포그래픽과 해설 · 표본 명식에 맞춰 책 빌더에 쓴 글' },
  daeun: { name: '대운', note: '대운 10편 · 명식 계산과 손으로 쓴 대운 원고' },
  seun: { name: '세운', note: '연도별 세운 · 명식 계산과 손으로 쓴 세운 원고' },
  wolun: { name: '월운', note: '절기 12개월 · 명식의 월운 계산과 십신별 고정 해설' },
  demo: { name: '추가 입력 안내', note: '상대·날짜 같은 추가 입력이 오면 채우는 서식 · 아직 계산하지 않습니다' },
  appendix: { name: '경험 대조·부록', note: '워크시트·계산식·계산하지 않은 것·판권 · 모든 책이 같은 틀' },
});

function chapterKind(pages, index) {
  const { layout } = pages[index];
  // A part title opens the part it names: the part of the page after it.
  if (layout === 'part-title') return index + 1 < pages.length ? chapterKind(pages, index + 1) : null;
  // The builder's demo_forms follows its form with a worksheet to use now; that worksheet is the demo's.
  if (layout === 'worksheet' && pages[index - 1]?.layout === 'demo-form') return 'demo';
  return CHAPTER_OF_LAYOUT[layout] ?? null;
}

/**
 * The parts of a book its template does not hold, in book order:
 * [{ id, kind, name, title, note, pages: [numbers] }]. A part that opens with a
 * part title is numbered as the book numbers it (1부 대운, 2부 세운, …) and titled by it.
 */
export function bookChapters(pages) {
  const chapters = [];
  let parts = 0;
  pages.forEach((page, index) => {
    const kind = chapterKind(pages, index);
    if (!kind) return;
    const last = chapters.at(-1);
    const opens = page.layout === 'part-title';
    if (opens || !last || last.kind !== kind || last.pages.at(-1) !== page.number - 1) {
      const { name, note } = CHAPTERS[kind];
      if (opens) parts += 1;
      chapters.push({ id: `${kind}-${page.number}`, kind, name: opens ? `${parts}부 ${name}` : name, title: opens ? page.title : name, note, pages: [] });
    }
    chapters.at(-1).pages.push(page.number);
  });
  return chapters;
}

/** The pages, each with the chapter it belongs to (null for a page of the template's own). */
function withChapters(pages) {
  const chapterOf = new Map(bookChapters(pages).flatMap(chapter => chapter.pages.map(number => [number, chapter])));
  return pages.map(page => ({ ...page, chapter: chapterOf.get(page.number) ?? null }));
}

// What each page template is called in the page list. A page of a layout not named here is called by its own title.
const TEMPLATE_NAMES = Object.freeze({
  'product-cover': '표지', 'book-boundary': '책 머리 · 계산 경계', 'book-contents': '책 머리 · 차례',
  'chart-palja': '명식 카드', 'chart-relations': '합충 카드', 'chart-daeun': '대운 카드', 'chart-seun': '세운 카드',
  'chart-seun-century': '100년 세운 카드', 'chart-wolun': '월운 카드', 'product-mechanism': '원인',
  'native-personality-strengths-cautions': '성격 분석', 'personality-inner-tension': '성격 · 두 마음',
  'native-wealth-four-traits': '재물운', 'wealth-value-boundary': '재물 · 노력과 내 몫',
  'product-comparison': '비교', 'product-advice': '조언', 'native-daeun-open-timeline': '대운 흐름', 'daeun-read': '대운 한 편',
  'daeun-advice': '대운 조언', 'seun-map': '세운 곡선', 'seun-phase': '세운 국면', 'seun-year': '세운 한 해',
  'century-decade': '100년 세운 · 10년 지도', 'wolun-table': '월운 한눈 표', 'wolun-quarter': '월운 세 달',
  'demo-form': '추가 입력 서식', worksheet: '워크시트', formula: '산식', uncalculated: '계산하지 않은 것', colophon: '판권',
});

/**
 * A book's pages as the page templates they are printed from, in book order: the pages of one layout
 * in one part of the book share a template (대운 한 편 = the ten daeun-read pages), each page filling it
 * with its own manuscript. [{ id, layout, title, chapter, pages: [numbers] }]
 */
export function pageTemplates(pages) {
  const templates = new Map();
  for (const page of withChapters(pages)) {
    const key = `${page.chapter?.kind ?? 'core'}:${page.layout}`;
    if (!templates.has(key)) {
      const name = TEMPLATE_NAMES[page.layout] ?? page.title;
      const title = page.layout === 'part-title' ? `${page.chapter?.name ?? '부'} · 부 제목`
        : page.chapter?.kind === 'demo' && page.layout === 'worksheet' ? '추가 입력 · 워크시트' : name;
      templates.set(key, { id: key, layout: page.layout, title, chapter: page.chapter ?? null, pages: [] });
    }
    templates.get(key).pages.push(page.number);
  }
  return [...templates.values()].sort((a, b) => a.pages[0] - b.pages[0]);
}

/** The Saju product a Studio document was made from, or null. */
export function sajuProductOf(content) {
  const title = content?.sections?.find(section => section?.origin?.templateId === 'saju-editorial')?.origin.sourceTitle;
  return SAJU_PRODUCTS.find(product => product.title === title) ?? null;
}

// The text the books print around their template is kept outside a product's Studio document: the
// 대운·세운 manuscripts (series/daeun/4/story, their score notes series/formula/<chapter>), the frame
// words (frame/<key>, template-saju-book.mjs), the chart cards' text (catalog/chart/<kind>/<field>),
// a product's lens (copy/<product>/lens) and the product catalogue's lines
// (catalogue/<product>/<field>/<n>). The Studio edits one where a page prints it, by the address its
// part names; edits wait on the local workshop until 책에 반영 writes them where the text is kept
// (apply-book-edits.mjs).
const FIELD_NAMES = Object.freeze({ title: '제목', lead: '도입', story: '해설', foundation: '원문 근거', reason: '변화 이유', advice: '조언', text: '글' });
const MANUSCRIPT = /^series\/(daeun|seun)\/(\d{1,4})\/(?:(title|lead|story|foundation|reason|advice)|traits\/([0-2])\/(title|text))$/;
const FORMULA = /^series\/formula\/(daeun|seun)$/;
const CHART = /^catalog\/chart\/([a-z-]+)\/(heading|body)$/, LENS = /^copy\/([a-z0-9-]+)\/lens$/;
const CATALOGUE = /^catalogue\/([a-z0-9-]+)\/(proposedSections|additionalInputs)\/(\d{1,2})$/;
const productTitle = id => SAJU_PRODUCTS.find(product => product.id === id)?.title ?? null;
/** The text an address names in the Studio's own module, or undefined when it is kept elsewhere (or nowhere). */
function moduleText(address) {
  const chart = CHART.exec(address), lens = LENS.exec(address);
  if (chart) return SAJU_CHARTS[chart[1]]?.[chart[2]];
  if (lens) return SAJU_PRODUCT_COPY[lens[1]]?.lens;
  return undefined;
}
const FRAME_WORD = /^frame\/([a-z][a-z0-9-]*\.[a-z0-9][a-z0-9._-]*)$/;
const FILLED = /\{([a-z_][a-z0-9_]*)\}/g;
const frameKey = address => { const key = FRAME_WORD.exec(address)?.[1]; return key && Object.hasOwn(SAJU_BOOK_FRAME, key) ? key : null; };
/** The values a frame text's {names} leave for the book to fill in. */
export const filledNames = text => [...String(text).matchAll(FILLED)].map(match => match[1]);

/** 대운 원고 · 4대운 해설, 틀 문구 · 표지 · 이 상품이 답하는 질문; null for an address that is not the books' text. */
export function bookTextLabel(address) {
  const key = frameKey(address);
  if (key) return `틀 문구 · ${SAJU_BOOK_FRAME[key].label}`;
  const formula = FORMULA.exec(address), chart = CHART.exec(address), lens = LENS.exec(address), line = CATALOGUE.exec(address);
  if (formula) return `${formula[1] === 'daeun' ? '대운' : '세운'} 원고 · 지수 설명`;
  if (chart && SAJU_CHARTS[chart[1]]) return `차트 설명 · ${SAJU_CHARTS[chart[1]].heading.split(' · ')[0]} ${chart[2] === 'heading' ? '제목' : '본문'}`;
  if (lens && productTitle(lens[1])) return `읽는 관점 · ${productTitle(lens[1])}`;
  if (line && productTitle(line[1])) return `상품 카탈로그 · ${productTitle(line[1])} · ${line[2] === 'proposedSections' ? '구성' : '추가 입력'} ${Number(line[3]) + 1}`;
  const match = MANUSCRIPT.exec(address);
  if (!match) return null;
  const [, series, record, field, trait, traitField] = match;
  const head = series === 'daeun' ? `대운 원고 · ${record}대운` : `세운 원고 · ${record}년`;
  return trait === undefined ? `${head} ${FIELD_NAMES[field]}` : `${head} 특징 ${Number(trait) + 1} ${FIELD_NAMES[traitField]}`;
}

// The product's own fields a page prints from the Studio document (doc/heading, doc/beats/0/title, …):
// the same text the canvas holds, edited in the document in place; 책에 반영 writes them into the
// product's template like an edit made on the canvas.
const DOC_LABELS = Object.freeze({ heading: '표지 제목', lead: '표지 도입', question: '상품 질문', mechanism: '원인', scene: '장면', comparison: '비교', advice: '조언' });
function docLabel(address) {
  const beat = /^doc\/beats\/([0-2])\/(title|text)$/.exec(address);
  return `상품 글 · ${beat ? `핵심 특징 ${Number(beat[1]) + 1} ${beat[2] === 'title' ? '제목' : '본문'}` : DOC_LABELS[address.slice(4)]}`;
}

let publishedRevision = null;
const publishedPages = () => import(publishedRevision === null ? './saju-book-pages.mjs' : `./saju-book-pages.mjs?published=${publishedRevision}`);
let manuscripts = null;
/** The published text kept outside the module: { texts: { address: text }, seunScenes: { year: [the picture of each 특징] } }. */
function publishedManuscripts() {
  manuscripts ??= publishedPages().then(async ({ SAJU_BOOK_PAGE_ROOT, SAJU_BOOK_TEXTS }) => {
    const response = await fetch(`${SAJU_BOOK_PAGE_ROOT}/${SAJU_BOOK_TEXTS}`);
    if (!response.ok) throw new Error(`대운·세운 원고를 불러오지 못했습니다 (${response.status}).`);
    return response.json();
  }).catch(error => { manuscripts = null; throw error; });
  return manuscripts;
}

// The edits of the books' design, not their text: a layout's values and the pictures (book-layouts.json, book-pictures.json).
const DESIGN = /^(layout|picture)\//;
// What each meaning is (an icon shows a meaning: a card whose trait reads as 표현 shows the 표현 icon).
const MEANINGS = Object.freeze({ expression: '표현', value: '가치', standards: '기준', understanding: '이해', growth: '성장', choice: '선택',
  relationship: '관계', thought: '생각', resources: '자원', management: '관리' });
/** Where a picture is chosen, said: what it is, and what else changes with it. */
function pictureLabel(address) {
  const icon = /^picture\/icon\/([a-z]+)$/.exec(address);
  if (icon) return { label: `아이콘 · ${MEANINGS[icon[1]] ?? icon[1]}`, scope: '이 콘텐츠 · 이 뜻의 아이콘이 나오는 모든 쪽' };
  if (/^picture\/product\/[a-z0-9-]+\/scene$/.test(address)) return { label: '표지 그림', scope: '이 상품만' };
  const topic = /^picture\/product\/[a-z0-9-]+\/topic\/(\d)$/.exec(address);
  if (topic) return { label: `원인 카드 ${Number(topic[1]) + 1}의 뜻${topic[1] === '2' ? ' · 조언 아이콘도' : ''}`, scope: '이 상품만 · 뜻을 고르면 그 뜻의 아이콘이 나옵니다' };
  return { label: address, scope: '' };
}
let pictureLibrary = null;

async function workshopEdits(path = '', body = null) {
  const response = await fetch(`${WORKSHOP}/edits${path}`, body ? { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : { cache: 'no-store' });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error ?? `원고 편집을 처리하지 못했습니다 (${response.status}).`);
  return result.edits;
}

let workshopAnswer = null;
/** Whether the local workshop answers; asked once per page load. */
function workshopAvailable() {
  workshopAnswer ??= fetch(`${WORKSHOP}/status`, { cache: 'no-store' })
    .then(async response => response.ok && (await response.json()).available === true)
    .catch(() => false);
  return workshopAnswer;
}

export const sajuBookOutput = Object.freeze({
  id: 'saju-book',
  path: 'book',
  noun: '책',
  linkedLabel: '템플릿 글',
  dataLabel: '데이터로 생성',
  dataNote: '대운·세운 원고와 책의 틀 문구는 그 쪽을 열어 오른쪽 ‘이 쪽의 글’에서 고치고, 명식 계산 값은 표본 명식을 따릅니다.',
  subjectOf(content) {
    const product = sajuProductOf(content);
    return product && { id: product.id, title: product.title };
  },
  describePreview(result, changed) {
    // The pending edits it was built with: texts, and the 틀 디자인's layout values.
    const designs = (result.manuscripts ?? []).filter(address => DESIGN.test(address)).length;
    const texts = (result.manuscripts ?? []).length - designs;
    const pending = [texts ? `고친 글 ${texts}개` : '', designs ? `틀 디자인 ${designs}곳` : ''].filter(Boolean).map(part => ` · ${part}`).join('');
    const made = !designs ? '글로' : texts || result.edited.length ? '글과 디자인으로' : '디자인으로';
    return `편집한 ${made} 만든 책입니다. 바뀐 칸: ${result.edited.length ? result.edited.join(', ') : '없음'}${pending} · 게시본과 다른 쪽 ${changed}개.`;
  },
  /** How many pages the published book has, or null when it is not published. */
  async pageCount(subject) {
    const { SAJU_BOOK_PAGES } = await publishedPages();
    return SAJU_BOOK_PAGES[subject.id]?.pages.length ?? null;
  },
  /** How many page templates the published book's pages are laid out from (its page list), or null when it is not published. */
  async pageTemplateCount(subject) {
    const { SAJU_BOOK_PAGES } = await publishedPages();
    const book = SAJU_BOOK_PAGES[subject.id];
    return book ? pageTemplates(book.pages).length : null;
  },
  async published(subject) {
    const { SAJU_BOOK_PAGES, SAJU_BOOK_PAGE_ROOT } = await publishedPages();
    const book = SAJU_BOOK_PAGES[subject.id];
    if (!book) throw new Error(`${subject.title} 책이 아직 게시되지 않았습니다.`);
    // Without the local server only the thumbnails exist; they stand in for the large pages.
    const local = await workshopAvailable();
    // Each page's parts (구성 보기) are in the book's parts file, fetched when first shown.
    return { note: '마지막으로 게시한 책(표본 명식) 기준입니다.', pages: withChapters(book.pages.map(page => ({ ...page, image: `${SAJU_BOOK_PAGE_ROOT}/${page.file}`,
      large: local ? `${WORKSHOP}/published/${subject.id}/page-${String(page.number).padStart(3, '0')}.png?published=${book.pdfSha256}` : null,
      partsUrl: `${SAJU_BOOK_PAGE_ROOT}/${book.parts}` }))) };
  },
  // The book lays its pages out itself: the Studio's page list is its page templates, each edited on its pages.
  templatesOf: pages => pageTemplates(pages),
  // The kinds of part the builder records on its pages (build-product-samples.py Book.parts): what the
  // color of each one's box on a page means.
  partKinds: Object.freeze({
    template: Object.freeze({ label: '상품 글', note: '이 상품만의 글 · 이 제작실 문서에 저장되고, 이 상품의 책에만 실립니다', color: '#0f8a6c' }),
    catalog: Object.freeze({ label: '카탈로그 글', note: '차트 설명·읽는 관점·상품 카탈로그 · 그 글을 싣는 책들이 함께 씁니다(상품 이름은 상품 목록에서 바꿉니다)', color: '#c68909' }),
    series: Object.freeze({ label: '대운·세운 원고', note: '18권이 함께 쓰는 원고 · 고치면 그 대운·그 해를 싣는 모든 책이 바뀝니다', color: '#8e5fe0' }),
    frame: Object.freeze({ label: '책의 틀 문구', note: '18권이 함께 쓰는 제목·안내·워크시트 문구', color: '#b5487a' }),
    component: Object.freeze({ label: '앱 컴포넌트', note: '사주 앱 화면(React 컴포넌트)을 그대로 캡처한 것 · 글로 고치지 않습니다', color: '#2479e8' }),
    image: Object.freeze({ label: '그림·틀', note: '배경·틀·명판·장면 그림', color: '#7c858f', outline: true }),
    computed: Object.freeze({ label: '명식 계산 값', note: '명식 데이터로 채운 값(간지·나이·점수) · 고치는 글이 아니라 표본 명식을 따릅니다', color: '#e0564a' }),
  }),
  /**
   * The product's fields as a page editor over the Studio document `document()`, edited in place and
   * then `changed(address)`; the same shape as `edits`, the text kept outside the document.
   */
  documentEdits({ document, changed }) {
    const now = address => sajuDocText(document(), address);
    const original = address => sajuDocText(document(), address, { original: true });
    return Object.freeze({
      note: '상품 글은 이 문서에 바로 저장되고, 이 상품의 책에만 실립니다.',
      available: async () => Boolean(sajuProductOf(document())),
      owns: isSajuDocField,
      label: docLabel,
      list: async () => SAJU_DOC_FIELDS.filter(address => now(address) !== original(address)).map(address => ({ address, base: original(address), value: now(address) })),
      load: async address => ({ text: now(address), published: original(address), pending: now(address) !== original(address), picture: null, fills: [] }),
      async save({ address, value }) { setSajuDocText(document(), address, value); changed(address); return { pending: now(address) !== original(address) }; },
      async revert(address) { setSajuDocText(document(), address, original(address)); changed(address); },
    });
  },
  // Text on its pages edited where it is printed (output-parts.mjs): the manuscripts.
  edits: Object.freeze({
    note: '원고·틀 문구·차트 설명은 여러 책이 함께 씁니다. 책에 반영하면 그 글을 싣는 모든 책이 바뀝니다.',
    target: '원고',
    available: workshopAvailable,
    owns: address => MANUSCRIPT.test(address) || FORMULA.test(address) || Boolean(frameKey(address)) || moduleText(address) !== undefined
      || (CATALOGUE.test(address) && Boolean(productTitle(CATALOGUE.exec(address)[1]))),
    label: bookTextLabel,
    /** The pending edits of the text: [{ address, base, value, reviewed? }] (the layouts' are layoutEdits'). */
    list: async () => (await workshopEdits()).filter(edit => !DESIGN.test(edit.address)),
    /**
     * What an address shows now ({ text, pending }), the {names} a frame text leaves for the book to
     * fill in, and for a 세운 특징 the picture paired with it.
     */
    async load(address, known = null) {
      const pendingEdits = known ?? await sajuBookOutput.edits.list();
      const key = frameKey(address), kept = key ? SAJU_BOOK_FRAME[key].text : moduleText(address);
      if (kept !== undefined) {
        const edit = pendingEdits.find(item => item.address === address) ?? null;
        return { text: edit ? edit.value : kept, published: kept, pending: Boolean(edit), picture: null, fills: filledNames(kept) };
      }
      const { texts, seunScenes } = await publishedManuscripts();
      const pending = pendingEdits;
      if (!Object.hasOwn(texts, address)) throw new Error(`${bookTextLabel(address) ?? address}: 게시한 원고에 없는 글입니다.`);
      const edit = pending.find(item => item.address === address) ?? null;
      const [, series, record, , trait] = MANUSCRIPT.exec(address) ?? [];
      const picture = series === 'seun' && trait !== undefined ? seunScenes[record]?.[Number(trait)] ?? null : null;
      return { text: edit ? edit.value : texts[address], published: texts[address], pending: Boolean(edit), picture, fills: [] };
    },
    save: ({ address, base, value, reviewed = false }) => workshopEdits('', { address, base, value, reviewed }),
    revert: address => workshopEdits('/revert', { address }),
  }),
  // The layouts its pages are drawn from, kept as data (book-layouts.json in the Saju app): their values are
  // moved and sized on the page (틀 디자인) and wait on the local workshop beside the text's until 책에 반영.
  // The books' design: the layouts' values and the pictures — changed where each is chosen.
  canvas: true,
  designEdits({ document, changed }) { return {
    ...documentDesignEdits({ document, changed, outputId: 'saju-book' }),
    note: '틀·그림 디자인은 이 콘텐츠에 저장됩니다. 같은 틀을 쓰는 이 책의 쪽에 적용됩니다.',
    target: '이 콘텐츠',
    /** The pictures a picture may be swapped for ({ pictures: [{ file, w, h }], icons: { meaning: file } }), asked once. */
    async pictures() {
      pictureLibrary ??= fetch(`${WORKSHOP}/pictures`, { cache: 'no-store' }).then(async response => {
        if (!response.ok) throw new Error(`그림 목록을 불러오지 못했습니다 (${response.status}).`);
        return response.json();
      }).catch(error => { pictureLibrary = null; throw error; });
      return pictureLibrary;
    },
    pictureUrl: file => `${WORKSHOP}/pictures/${file.split('/').map(encodeURIComponent).join('/')}`,
    pictureLabel,
    meaningName: meaning => MEANINGS[meaning] ?? meaning,
  }; },
  designOptions: Object.freeze({
    heading: '캔버스 디자인',
    picturesNote: '그림은 이 콘텐츠에서 바뀝니다. 뜻의 아이콘을 바꾸면 같은 뜻의 아이콘이 나오는 모든 쪽에, 상품 그림을 바꾸면 해당 상품의 쪽에 반영됩니다.',
    ranges: { size: [8, 60] },
    defaults: { text: { x: 60, y: 860, w: 640, height: 100, size: 20, font: 'body', color: 'ivory' }, art: { x: 60, y: 860, w: 120 } },
    kindLabels: { 'feature-card': { size: '본문 글자 크기' } },
    propLabels: { 'style.icon': '아이콘 크기', 'style.iconTop': '아이콘 위 여백', 'style.titleSize': '제목 글자 크기', 'style.textSize': '본문 글자 크기',
      'style.icon.s': '아이콘 크기', 'style.icon.dx': '아이콘 가로', 'style.icon.dy': '아이콘 세로', 'style.title.dy': '제목 세로', 'style.title.size': '제목 글자 크기',
      'style.text.dy': '본문 세로', 'style.text.size': '본문 글자 크기' },
    scope: ({ found }) => `이 콘텐츠의 같은 틀 ${found.layoutPages[found.layoutId] ?? 1}쪽 · 템플릿 저장에 포함됩니다`,
    editableNote: '상자를 끌어 옮기고 모서리로 크기를 바꿉니다. 미리보기로 저장한 디자인을 확인합니다.',
    canEditText: definition => !definition.text?.includes('{'),
  }),
  layoutChoices: Object.freeze({ font: Object.freeze({ body: '본문(돋움)', bold: '굵은 돋움', serif: '명조' }), color: Object.freeze({ ivory: '아이보리', gold: '금색', navy: '남색' }) }),
  workshop: Object.freeze({
    available: workshopAvailable,
    async preview(documentValue, onProgress) {
      const result = await runOutputJob(`${WORKSHOP}/preview`, documentValue, onProgress, { designEdits: documentValue.outputDesigns?.['saju-book']?.edits ?? [] });
      return { title: result.title, edited: result.edited, manuscripts: result.manuscripts ?? [], pages: withChapters(result.pages.map(page => ({ ...page, image: page.url, large: page.large }))) };
    },
    async apply(documentValue, onProgress) {
      const result = await runOutputJob(`${WORKSHOP}/apply`, documentValue, onProgress, { designEdits: documentValue.outputDesigns?.['saju-book']?.edits ?? [] });
      // Imports and parts are cached for a reading session. A completed publish has new
      // content-hashed parts/thumbnails; reload their index before editing against them.
      publishedRevision = crypto.randomUUID();
      manuscripts = null;
      pictureLibrary = null;
      return result;
    },
    previewLabel: '이 편집으로 책 미리보기',
    applyLabel: '책에 반영 (로컬)',
    applyConfirm: '이 편집을 사주 템플릿 원본에 쓰고 18권을 다시 만들어 게시합니다(로컬, 수 분). 계속할까요?',
  }),
});
